import { useCallback, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ScanTextIcon } from 'lucide-react';
import { PERMISSIONS } from '@siteflow/shared';
import { ContentFormRenderer } from '@/components/content/content-form-renderer';
import { ScanDialog } from '@/components/content/scan-dialog';
import { VersionHistoryDrawer } from '@/components/content/version-history-drawer';
import { PermissionGate } from '@/components/permission-gate';
import { EnvironmentSelect, LocaleSelect } from '@/components/sites/environment-select';
import { useSiteScope } from '@/components/sites/site-scope';
import { CONTENT_ONLY } from '@/lib/ui-mode';
import { Button } from '@/components/ui/button';
import { contentApi, contentKeys, entriesQuery } from '@/lib/api/content';
import type { ContentEntry, EntryInput } from '@/lib/api/types';
import { useOrgPermissions } from '@/lib/permissions';

function GlobalContent() {
  const { orgId, siteId } = Route.useParams();
  const queryClient = useQueryClient();
  const { site, environments, environmentId, setEnvironmentId, locale, setLocale } = useSiteScope();
  const { has } = useOrgPermissions(orgId);
  const canEdit = has(PERMISSIONS.CONTENT_EDIT);
  const canViewHistory = has(PERMISSIONS.CONTENT_VERSIONS_VIEW);

  const [scanOpen, setScanOpen] = useState(false);
  const [historyEntry, setHistoryEntry] = useState<ContentEntry | null>(null);

  const entries = useQuery(entriesQuery(orgId, siteId, { global: true, locale, environmentId }));

  const save = useCallback(
    (batch: EntryInput[]) => contentApi.saveEntries(orgId, siteId, batch),
    [orgId, siteId],
  );

  const onSaved = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: contentKeys.all(orgId, siteId) });
  }, [orgId, queryClient, siteId]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-prose">
          <h2 className="text-lede font-semibold tracking-tight text-ink">
            {CONTENT_ONLY ? 'Shared content' : 'Site-wide content'}
          </h2>
          <p className="text-data leading-relaxed text-ink-muted">
            {CONTENT_ONLY
              ? 'Text that appears across the whole website, such as menus, the footer and contact details. A change here shows up on every page.'
              : 'Entries that belong to no single URL — navigation labels, footer details, contact information. They are served to every page of the site, so a change here shows up everywhere at once.'}
          </p>
        </div>
        {CONTENT_ONLY ? null : (
          <PermissionGate permission={PERMISSIONS.CONTENT_EDIT}>
            <Button variant="secondary" size="sm" onClick={() => setScanOpen(true)}>
              <ScanTextIcon />
              Scan for content keys
            </Button>
          </PermissionGate>
        )}
      </div>

      <ContentFormRenderer
        orgId={orgId}
        siteId={siteId}
        entries={entries.data ?? []}
        pageSlug={null}
        locale={locale}
        canEdit={canEdit}
        isLoading={entries.isLoading}
        error={entries.isError ? entries.error : undefined}
        onSave={save}
        onSaved={onSaved}
        onHistory={canViewHistory ? setHistoryEntry : undefined}
        toolbar={
          <>
            {CONTENT_ONLY ? null : (
              <EnvironmentSelect
                environments={environments}
                value={environmentId}
                onChange={setEnvironmentId}
                label="Status for"
              />
            )}
            <LocaleSelect locales={site.locales} value={locale} onChange={setLocale} />
          </>
        }
      />

      <ScanDialog
        open={scanOpen}
        onOpenChange={setScanOpen}
        orgId={orgId}
        siteId={siteId}
        pageSlug={null}
        canRegister={canEdit}
      />

      <VersionHistoryDrawer
        open={historyEntry !== null}
        onOpenChange={(open) => !open && setHistoryEntry(null)}
        orgId={orgId}
        siteId={siteId}
        entry={historyEntry}
      />
    </div>
  );
}

export const Route = createFileRoute('/app/$orgId/sites/$siteId/content')({
  component: GlobalContent,
});
